const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);

const rangeProgress = (progress, start, end) => clamp((progress - start) / (end - start));

const fadeWindow = (progress, enterStart, enterEnd, leaveStart, leaveEnd) => {
  const enter = rangeProgress(progress, enterStart, enterEnd);
  const leave = 1 - rangeProgress(progress, leaveStart, leaveEnd);

  return Math.min(enter, leave);
};

const getOverlayCollisionOpacity = (copy, overlay) => {
  if (!copy || !overlay) {
    return 1;
  }

  const fadeDistance = window.matchMedia('(max-width: 768px)').matches ? 64 : 200;
  const copyBottom = (window.innerHeight + copy.offsetHeight) / 2;
  const distance = overlay.getBoundingClientRect().top - copyBottom;

  return clamp(distance / fadeDistance);
};

const setCopyState = (element, opacity) => {
  if (!element) {
    return;
  }

  const easedOpacity = clamp(opacity);
  const isBottomAligned = element.classList.contains('top-story__mv-content');

  element.style.opacity = easedOpacity.toFixed(4);
  element.style.transform = isBottomAligned
    ? `translate3d(-50%, ${((1 - easedOpacity) * 18).toFixed(2)}px, 0)`
    : 'translate3d(-50%, -50%, 0)';
  element.style.visibility = easedOpacity < 0.01 ? 'hidden' : 'visible';
};

const setSceneState = (element, progress, reduceMotion) => {
  if (!element) {
    return;
  }

  const reveal = clamp(progress);
  const image = element.querySelector('.top-story__media');
  const layerHeight = element.getBoundingClientRect().height;
  const feather = clamp(layerHeight * 0.16, 110, 220);
  const boundary = layerHeight * (1 - reveal);
  const featherStart = Math.max(boundary - feather, 0);
  const featherEnd = Math.min(boundary + feather, layerHeight);

  element.style.setProperty('--story-reveal', `${(reveal * 100).toFixed(3)}%`);

  if (reveal <= 0) {
    element.style.clipPath = 'inset(100% 0 0)';
    element.style.webkitMaskImage = 'none';
    element.style.maskImage = 'none';
  } else if (reveal >= 1 || reduceMotion) {
    element.style.clipPath = 'none';
    element.style.webkitMaskImage = 'none';
    element.style.maskImage = 'none';
  } else {
    const mask = `linear-gradient(to bottom, transparent ${featherStart.toFixed(2)}px, #000 ${featherEnd.toFixed(2)}px)`;

    element.style.clipPath = 'none';
    element.style.webkitMaskImage = mask;
    element.style.maskImage = mask;
  }

  if (image && !reduceMotion) {
    image.style.setProperty('--story-shift', `${((1 - reveal) * 2.4).toFixed(3)}%`);
  }
};

const initTopStoryVideo = (video) => {
  if (!video) {
    return;
  }

  const isSpViewport = window.matchMedia('(max-width: 1020px)');
  const setVideoSource = () => {
    const source = isSpViewport.matches
      ? (video.dataset.videoSp || video.dataset.videoPc)
      : (video.dataset.videoPc || video.dataset.videoSp);

    if (!source || video.src === source) {
      return;
    }

    video.src = source;
    video.load();
    video.play().catch(() => {});
  };

  setVideoSource();
  isSpViewport.addEventListener('change', setVideoSource);
};

const createScrollTrack = (track, render, hold = null) => {
  if (!track) {
    return;
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let targetProgress = 0;
  let currentProgress = 0;
  let animationId = null;
  let trackTop = 0;
  let scrollDistance = 1;

  const getRenderProgress = (progress) => {
    if (!hold) {
      return progress;
    }

    const endHoldDistance = window.innerHeight * (hold.endViewportRatio || 0);
    const activeScrollDistance = Math.max(scrollDistance - endHoldDistance, 1);
    const traveledDistance = Math.min(progress * scrollDistance, activeScrollDistance);
    const holdDistance = window.innerHeight * hold.viewportRatio;
    const baseDistance = Math.max(activeScrollDistance - holdDistance, 1);
    const holdStart = baseDistance * hold.at;

    if (traveledDistance <= holdStart) {
      return clamp(traveledDistance / baseDistance);
    }

    if (traveledDistance <= holdStart + holdDistance) {
      return hold.at;
    }

    return clamp((traveledDistance - holdDistance) / baseDistance);
  };

  const measure = () => {
    const rect = track.getBoundingClientRect();

    trackTop = rect.top + window.scrollY;
    scrollDistance = Math.max(track.offsetHeight - window.innerHeight, 1);
  };

  const tick = () => {
    const difference = targetProgress - currentProgress;
    const smoothing = reduceMotion.matches ? 1 : 0.085;

    currentProgress += difference * smoothing;

    if (Math.abs(difference) < 0.0001) {
      currentProgress = targetProgress;
    }

    render(getRenderProgress(currentProgress), reduceMotion.matches);

    if (currentProgress !== targetProgress) {
      animationId = window.requestAnimationFrame(tick);
      return;
    }

    animationId = null;
  };

  const updateTarget = () => {
    targetProgress = clamp((window.scrollY - trackTop) / scrollDistance);

    if (animationId === null) {
      animationId = window.requestAnimationFrame(tick);
    }
  };

  const handleResize = () => {
    measure();
    updateTarget();
  };

  measure();
  targetProgress = clamp((window.scrollY - trackTop) / scrollDistance);
  currentProgress = targetProgress;
  render(getRenderProgress(currentProgress), reduceMotion.matches);

  window.addEventListener('scroll', updateTarget, {passive: true});
  window.addEventListener('resize', handleResize);
  reduceMotion.addEventListener('change', updateTarget);
};

const initTopStoryTrack = () => {
  const track = document.querySelector('.js-top-story');

  if (!track) {
    return;
  }

  const introScene = track.querySelector('[data-intro-scene="intro"]');
  const scenes = {
    spring: track.querySelector('[data-season-scene="spring"]'),
    summer: track.querySelector('[data-season-scene="summer"]'),
    autumn: track.querySelector('[data-season-scene="autumn"]'),
    winter: track.querySelector('[data-season-scene="winter"]'),
  };
  const mvCopy = track.querySelector('[data-intro-copy="mv"]');
  const introCopy = track.querySelector('[data-intro-copy="intro"]');
  const overview = document.querySelector('.top-overview');
  const copies = {
    spring: track.querySelector('[data-season-copy="spring"]'),
    summer: track.querySelector('[data-season-copy="summer"]'),
    autumn: track.querySelector('[data-season-copy="autumn"]'),
    winter: track.querySelector('[data-season-copy="winter"]'),
  };
  const video = track.querySelector('.js-top-story-video');
  initTopStoryVideo(video);

  createScrollTrack(track, (progress, reduceMotion) => {
    setSceneState(introScene, rangeProgress(progress, 0.083, 0.141), reduceMotion);
    setSceneState(scenes.spring, rangeProgress(progress, 0.397, 0.471), reduceMotion);
    setSceneState(scenes.summer, rangeProgress(progress, 0.528, 0.595), reduceMotion);
    setSceneState(scenes.autumn, rangeProgress(progress, 0.669, 0.735), reduceMotion);
    setSceneState(scenes.winter, rangeProgress(progress, 0.81, 0.876), reduceMotion);

    setCopyState(mvCopy, 1 - rangeProgress(progress, 0.066, 0.124));
    const introOpacity = Math.min(
      rangeProgress(progress, 0.124, 0.157),
      getOverlayCollisionOpacity(introCopy, overview),
    );

    setCopyState(introCopy, introOpacity);
    setCopyState(copies.spring, fadeWindow(progress, 0.454, 0.488, 0.52, 0.553));
    setCopyState(copies.summer, fadeWindow(progress, 0.578, 0.612, 0.66, 0.693));
    setCopyState(copies.autumn, fadeWindow(progress, 0.719, 0.752, 0.801, 0.834));
    setCopyState(copies.winter, fadeWindow(progress, 0.86, 0.894, 1.05, 1.1));

    if (video && video.currentSrc) {
      if (progress < 0.141 && video.paused) {
        video.play().catch(() => {});
      } else if (progress >= 0.141 && !video.paused) {
        video.pause();
      }
    }
  }, {
    // 春のコピーが完全に表示された位置で、40svh分だけ進行を止める。
    at: 0.52,
    viewportRatio: 0.4,
    // 冬の表示後、後続のお知らせセクションが画面を覆うまで1画面分固定する。
    endViewportRatio: 1,
  });
};

const initTopHeader = () => {
  const header = document.querySelector('.header');
  const story = document.querySelector('.js-top-story');

  if (!header || !story) {
    return;
  }

  const isSpViewport = window.matchMedia('(max-width: 1020px)');
  let headerShowPoint = 0;
  let updateScheduled = false;

  const measureHeaderShowPoint = () => {
    headerShowPoint = story.getBoundingClientRect().top + window.scrollY + window.innerHeight;
  };

  const updateHeaderState = () => {
    const isHeaderVisible = window.scrollY >= headerShowPoint;
    const isTopMenuVisible = isSpViewport.matches && !isHeaderVisible;

    header.classList.toggle('is-visible', isHeaderVisible);
    header.classList.toggle('is-top-menu-visible', isTopMenuVisible);
    updateScheduled = false;
  };

  const scheduleHeaderUpdate = () => {
    if (updateScheduled) {
      return;
    }

    updateScheduled = true;
    window.requestAnimationFrame(updateHeaderState);
  };

  const handleViewportResize = () => {
    measureHeaderShowPoint();
    scheduleHeaderUpdate();
  };

  measureHeaderShowPoint();
  updateHeaderState();

  window.addEventListener('scroll', scheduleHeaderUpdate, {passive: true});
  window.addEventListener('resize', handleViewportResize);
};

const initNewsAnchorVisibility = () => {
  const anchor = document.querySelector('.top-news__anchor');
  const targets = document.querySelectorAll('.top-information, .footer');

  if (!anchor || !targets.length) {
    return;
  }

  // 重要なお知らせ表示中は、KV内かどうかをクラスで渡す（SPのみCSSでKV内は非表示にする）。
  // 判定位置はヘッダーの表示切り替えと同じ「ストーリー先頭 + 1画面分」。
  const story = document.querySelector('.js-top-story');

  if (anchor.classList.contains('-has-important') && story) {
    let kvEndPoint = 0;
    let kvUpdateScheduled = false;

    const measureKvEndPoint = () => {
      kvEndPoint = story.getBoundingClientRect().top + window.scrollY + window.innerHeight;
    };

    const updateKvState = () => {
      anchor.classList.toggle('is-in-kv', window.scrollY < kvEndPoint);
      kvUpdateScheduled = false;
    };

    const scheduleKvUpdate = () => {
      if (kvUpdateScheduled) {
        return;
      }

      kvUpdateScheduled = true;
      window.requestAnimationFrame(updateKvState);
    };

    measureKvEndPoint();
    updateKvState();

    window.addEventListener('scroll', scheduleKvUpdate, {passive: true});
    window.addEventListener('resize', () => {
      measureKvEndPoint();
      scheduleKvUpdate();
    });
  }

  const intersectingTargets = new Set();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        intersectingTargets.add(entry.target);
      } else {
        intersectingTargets.delete(entry.target);
      }
    });

    anchor.classList.toggle('is-hidden', intersectingTargets.size > 0);
  });

  targets.forEach((target) => observer.observe(target));
};

export const initTopStory = () => {
  initTopStoryTrack();
  initTopHeader();
  initNewsAnchorVisibility();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTopStory, {once: true});
} else {
  initTopStory();
}
